(() => {
  const ROOT_SELECTOR = '[data-swalk-personaliser]';
  const TRIGGER_SELECTOR = '[data-swalk-personalise-trigger]';

  const PANEL_WIDTH = 1683;
  const PANEL_HEIGHT = 1712;
  const SAFE_INSET = 150;
  const SAFE_WIDTH = PANEL_WIDTH - SAFE_INSET * 2;
  const SAFE_HEIGHT = PANEL_HEIGHT - SAFE_INSET * 2;
  const SAFETY_BUFFER = 28;
  const PRINT_WIDTH = SAFE_WIDTH - SAFETY_BUFFER * 2;
  const PRINT_HEIGHT = SAFE_HEIGHT - SAFETY_BUFFER * 2;
  const LEGACY_FONT_SCALE = 5;
  const LINE_HEIGHT = 1.15;

  const FONT_FAMILIES = {
    abel: 'Abel, sans-serif',
    'amatic-sc': '"Amatic SC", cursive',
    'crete-round': '"Crete Round", serif',
    lobster: 'Lobster, cursive',
    parisienne: 'Parisienne, cursive'
  };

  const FONT_NAMES = {
    abel: 'Abel',
    'amatic-sc': 'Amatic SC',
    'crete-round': 'Crete Round',
    lobster: 'Lobster',
    parisienne: 'Parisienne'
  };

  const DRAFT_FIELDS = {
    message: { name: 'properties[Message]', value: '' },
    font: { name: 'properties[_SWALK Font]', value: 'abel' },
    'font-size': { name: 'properties[_SWALK Font Size]', value: '15' },
    alignment: { name: 'properties[_SWALK Alignment]', value: 'left' },
    'card-type': { name: 'properties[_SWALK Card Type]', value: 'Personalised' },
    'artwork-url': { name: 'properties[_SWALK Print Artwork]', value: '' },
    'artwork-format': { name: 'properties[_SWALK Artwork Format]', value: '' },
    'outer-artwork': { name: 'properties[_SWALK Outer Artwork]', value: '' },
    'inner-artwork': { name: 'properties[_SWALK Inner Artwork]', value: '' },
    'inner-pdf': { name: 'properties[_SWALK Inner Print PDF]', value: '' }
  };

  const REMOVED_LINE_PROPERTIES = [
    '_SWALK Artwork Signature',
    '_SWALK Fulfilment Route',
    '_SWALK Contract Version',
    '_SWALK Spare Envelope'
  ];

  let legacyFontsPromise = null;

  function ensureLegacyFonts() {
    if (legacyFontsPromise) return legacyFontsPromise;

    legacyFontsPromise = new Promise((resolve) => {
      const existing = document.querySelector('[data-swalk-personaliser-fonts]');
      if (existing) {
        if (existing.sheet) {
          resolve();
          return;
        }
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', resolve, { once: true });
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Abel&family=Amatic+SC:wght@400;700&family=Crete+Round&family=Lobster&family=Parisienne&display=swap';
      link.dataset.swalkPersonaliserFonts = 'true';
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', resolve, { once: true });
      document.head.appendChild(link);
    });

    return legacyFontsPromise;
  }

  async function ensureSelectedFont(fontKey, fontSize) {
    await ensureLegacyFonts();
    const family = FONT_NAMES[fontKey] || FONT_NAMES.abel;
    if (document.fonts?.load) {
      try {
        await document.fonts.load(`${fontSize}px "${family}"`);
      } catch {}
    }
  }

  function breakLongToken(context, token, maxWidth) {
    const chunks = [];
    let chunk = '';

    Array.from(token).forEach((character) => {
      const candidate = `${chunk}${character}`;
      if (chunk && context.measureText(candidate).width > maxWidth) {
        chunks.push(chunk);
        chunk = character;
      } else {
        chunk = candidate;
      }
    });

    if (chunk) chunks.push(chunk);
    return chunks;
  }

  function wrapCanvasText(context, text, maxWidth) {
    const lines = [];
    const paragraphs = String(text || '').split(/\r?\n/);

    paragraphs.forEach((paragraph) => {
      if (paragraph === '') {
        lines.push('');
        return;
      }

      const words = paragraph.trim().split(/\s+/);
      let line = '';

      words.forEach((word) => {
        if (context.measureText(word).width > maxWidth) {
          if (line) {
            lines.push(line);
            line = '';
          }

          const chunks = breakLongToken(context, word, maxWidth);
          chunks.forEach((chunk, index) => {
            if (index === chunks.length - 1) line = chunk;
            else lines.push(chunk);
          });
          return;
        }

        const candidate = line ? `${line} ${word}` : word;
        if (line && context.measureText(candidate).width > maxWidth) {
          lines.push(line);
          line = word;
        } else {
          line = candidate;
        }
      });

      if (line) lines.push(line);
    });

    return lines.length ? lines : [''];
  }

  function isPlainObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  }

  function normalizeCartProperties(value) {
    let entries = null;
    if (Array.isArray(value)) {
      if (!value.every((entry) => Array.isArray(entry) && entry.length === 2)) {
        throw new Error('The basket item properties are not valid.');
      }
      entries = value;
    } else if (isPlainObject(value)) {
      entries = Object.entries(value);
    } else {
      throw new Error('The basket item properties are not valid.');
    }

    const properties = {};
    entries.forEach(([key, propertyValue]) => {
      if (typeof key !== 'string' || !key || ['__proto__', 'prototype', 'constructor'].includes(key)) {
        throw new Error('The basket item properties are not valid.');
      }
      if (propertyValue !== null && !['string', 'number', 'boolean'].includes(typeof propertyValue)) {
        throw new Error('The basket item properties are not valid.');
      }
      properties[key] = propertyValue;
    });
    return properties;
  }

  class SwalkPersonaliser {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-swalk-personaliser-dialog]');
      this.textarea = root.querySelector('[data-swalk-message]');
      this.fontSelect = root.querySelector('[data-swalk-font]');
      this.sizeInput = root.querySelector('[data-swalk-font-size]');
      this.sizeValue = root.querySelector('[data-swalk-size-value]');
      this.previewPanel = root.querySelector('[data-swalk-preview-panel]');
      this.previewSafeArea = root.querySelector('[data-swalk-preview-safe-area]');
      this.previewMessage = root.querySelector('[data-swalk-preview-message]');
      this.overflowWarning = root.querySelector('[data-swalk-overflow-warning]');
      this.generationError = root.querySelector('[data-swalk-generation-error]');
      this.alignmentButtons = Array.from(root.querySelectorAll('[data-swalk-alignment]'));
      this.closeButtons = Array.from(root.querySelectorAll('[data-swalk-personaliser-close]'));
      this.continueButton = root.querySelector('[data-swalk-personaliser-continue]');
      this.isCartEditor = root.hasAttribute('data-swalk-cart-editor');
      this.productId = root.dataset.productId;
      this.artworkEndpoint = root.dataset.swalkArtworkEndpoint || '';
      this.artworkMap = this.readArtworkMap();
      this.variantSkuMap = this.readVariantSkuMap();
      this.lastTrigger = null;
      this.sourceForm = null;
      this.alignment = 'left';
      this.resizeObserver = null;
      this.generating = false;
      this.continueLabel = this.continueButton?.textContent || 'CONTINUE';
      this.lastValidMessage = this.textarea?.value || '';
      this.configuredMaximumSize = Number(this.sizeInput?.max || 30);
      this.lastValidSize = Number(this.sizeInput?.value || 15);
      this.fontChangeSequence = 0;
      this.editContext = null;
      this.draftArtworkSignature = '';

      this.bind();
      this.updatePreview();
    }

    get form() {
      if (this.isCartEditor) return null;
      if (this.sourceForm?.isConnected) return this.sourceForm;
      return document.querySelector('.product__buy-buttons form[data-type="add-to-cart-form"]');
    }

    readArtworkMap() {
      const node = this.root.querySelector('[data-swalk-artwork-map]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent || '{}');
      } catch (error) {
        console.error('SWALK artwork map could not be parsed:', error);
        return {};
      }
    }

    readVariantSkuMap() {
      const node = this.root.querySelector('[data-swalk-variant-sku-map]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent || '{}');
      } catch (error) {
        console.error('SWALK variant SKU map could not be parsed:', error);
        return {};
      }
    }

    resolveForm(trigger) {
      return trigger?.closest('.product__buy-buttons')?.querySelector('form[data-type="add-to-cart-form"]') ||
        document.querySelector('.product__buy-buttons form[data-type="add-to-cart-form"]');
    }

    ensureDraftFields() {
      const form = this.form;
      if (!form) return;

      Object.entries(DRAFT_FIELDS).forEach(([key, config]) => {
        let field = form.querySelector(`[data-swalk-personalisation-property="${key}"]`);
        if (field) return;

        field = document.createElement('input');
        field.type = 'hidden';
        field.name = config.name;
        field.value = config.value;
        field.disabled = true;
        field.dataset.swalkPersonalisationProperty = key;
        form.appendChild(field);
      });
    }

    bind() {
      if (this.isCartEditor) {
        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-swalk-edit-message]');
          if (!trigger) return;
          this.openCartEdit(trigger);
        });
      } else {
        document.addEventListener('click', (event) => {
          const trigger = event.target.closest(TRIGGER_SELECTOR);
          if (!trigger || !this.matchesTrigger(trigger)) return;
          this.open(trigger);
        });

        document.addEventListener('swalk:personaliser:open', (event) => {
          const trigger = event.detail?.trigger;
          if (!trigger || !this.matchesTrigger(trigger, event.detail?.productId)) return;
          this.open(trigger, event.detail?.returnFocus);
        });
      }

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.root.dataset.swalkShellStepActive !== 'destination') this.close();
      });

      this.textarea?.addEventListener('input', () => {
        this.textarea.removeAttribute('aria-invalid');
        if (!this.messageFitsPrintableArea(this.textarea.value)) {
          if (this.textarea.value.length < this.lastValidMessage.length) {
            this.lastValidMessage = this.textarea.value;
            this.markArtworkStale();
            this.updatePreview();
            return;
          }
          const selection = Math.min(this.lastValidMessage.length, this.textarea.selectionStart || 0);
          this.textarea.value = this.lastValidMessage;
          this.textarea.setSelectionRange(selection, selection);
          if (this.overflowWarning) this.overflowWarning.hidden = false;
          this.textarea.setAttribute('aria-invalid', 'true');
          this.updatePreview({ preserveWarning: true });
          return;
        }
        this.lastValidMessage = this.textarea.value;
        this.markArtworkStale();
        this.updatePreview();
      });

      this.fontSelect?.addEventListener('change', () => {
        this.markArtworkStale();
        this.handleFontChange();
      });

      this.sizeInput?.addEventListener('input', () => {
        const requestedSize = Number(this.sizeInput.value || 15);
        const message = this.textarea?.value || '';
        const font = this.fontSelect?.value || 'abel';
        const fits = this.measurePrintableLayout(message, font, requestedSize).fits;
        if (requestedSize > this.lastValidSize && !fits) {
          this.sizeInput.value = String(this.lastValidSize);
          if (this.overflowWarning) this.overflowWarning.hidden = false;
        } else {
          this.lastValidSize = requestedSize;
        }
        this.markArtworkStale();
        this.updatePreview({ preserveWarning: requestedSize > this.lastValidSize });
      });

      this.alignmentButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.alignment = button.dataset.swalkAlignment || 'left';
          this.alignmentButtons.forEach((candidate) => {
            candidate.setAttribute('aria-pressed', String(candidate === button));
          });
          this.markArtworkStale();
          this.updatePreview();
        });
      });

      this.continueButton?.addEventListener('click', () => this.handleContinue());

      if ('ResizeObserver' in window && this.previewPanel) {
        this.resizeObserver = new ResizeObserver(() => this.updatePreviewScale());
        this.resizeObserver.observe(this.previewPanel);
      } else {
        window.addEventListener('resize', () => this.updatePreviewScale(), { passive: true });
      }
    }

    matchesTrigger(trigger, productId = '') {
      const triggerProductId = String(productId || trigger.dataset.swalkProductId || '');
      return !triggerProductId || triggerProductId === String(this.productId || '');
    }

    openCartEdit(trigger) {
      let properties = {};
      try {
        properties = normalizeCartProperties(JSON.parse(trigger.dataset.swalkProperties || '{}'));
      } catch (error) {
        console.error('SWALK cart properties could not be read:', error);
        return;
      }

      const surface = trigger.closest('cart-drawer, cart-notification');
      const notificationItems = surface?.matches('cart-notification')
        ? surface.querySelector('#cart-notification-item')
        : null;
      this.editContext = {
        line: Number(trigger.dataset.swalkLine || 0),
        key: trigger.dataset.swalkLineKey || '',
        quantity: Number(trigger.dataset.swalkQuantity || 1),
        productId: String(trigger.dataset.swalkProductId || ''),
        variantId: String(trigger.dataset.swalkVariantId || ''),
        variantSku: String(trigger.dataset.swalkVariantSku || ''),
        baseArtworkUrl: trigger.dataset.swalkBaseArtworkUrl || '',
        artworkFormat: trigger.dataset.swalkArtworkFormat || 'legacy-prodigi-v1',
        properties,
        surface,
        notificationItems,
        notificationContent: notificationItems?.innerHTML || ''
      };
      this.lastTrigger = trigger;
      this.sourceForm = null;

      this.suspendCartEditSurface(surface);
      this.populateCartEdit(properties);
      this.hideGenerationError();
      this.showShellStep('message');
      this.root.hidden = false;
      document.body.classList.add('swalk-personaliser-open');
      document.body.classList.add('swalk-journey-open');
      this.updatePreview();

      ensureSelectedFont(this.fontSelect?.value || 'abel', LEGACY_FONT_SCALE * this.configuredMaximumSize)
        .then(() => {
          this.clampSizeToMessage();
          this.updatePreview();
        });

      requestAnimationFrame(() => {
        if (typeof window.trapFocus === 'function' && this.dialog) {
          window.trapFocus(this.dialog, this.textarea || this.dialog);
        } else {
          this.textarea?.focus();
        }
      });
    }

    suspendCartEditSurface(surface) {
      if (!surface) return;
      if (surface.matches('cart-notification')) {
        surface.removeAttribute('open');
        surface.classList.remove('modal--open');
        document.body.classList.remove('overflow-hidden-drawer');
        if (surface.header) surface.header.preventHide = false;
        if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(null);
        return;
      }
      surface.close?.();
    }

    populateCartEdit(properties) {
      this.fontChangeSequence += 1;
      if (this.textarea) {
        this.textarea.value = properties.Message || '';
        this.textarea.removeAttribute('aria-invalid');
      }
      if (this.overflowWarning) this.overflowWarning.hidden = true;
      this.lastValidMessage = this.textarea?.value || '';
      if (this.fontSelect) this.fontSelect.value = properties['_SWALK Font'] || 'abel';
      if (this.sizeInput) {
        this.sizeInput.min = '10';
        this.sizeInput.max = '30';
        this.sizeInput.value = properties['_SWALK Font Size'] || '15';
        this.lastValidSize = Number(this.sizeInput.value || 15);
      }
      this.alignment = properties['_SWALK Alignment'] || 'left';
      this.alignmentButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.swalkAlignment === this.alignment));
      });
    }

    async handleContinue() {
      if (this.generating) return;

      const message = (this.textarea?.value || '').trim();
      if (!message && this.getArtworkFormat() !== 'home-print-v1') {
        this.textarea?.focus();
        this.textarea?.setAttribute('aria-invalid', 'true');
        return;
      }

      if (this.previewDoesOverflow()) {
        if (this.overflowWarning) this.overflowWarning.hidden = false;
        this.textarea?.focus();
        return;
      }

      this.textarea?.removeAttribute('aria-invalid');
      this.hideGenerationError();
      if (!this.editContext) this.saveDraftFields();
      this.setGenerating(true);

      try {
        const artworkUrl = await this.generateArtwork();
        if (this.editContext) {
          await this.saveCartEdit({
            message,
            font: this.fontSelect?.value || 'abel',
            fontSize: Number(this.sizeInput?.value || 15),
            alignment: this.alignment,
            artworkUrl
          });
          this.setGenerating(false);
          this.close();
          return;
        }
        const variantId = this.getSelectedVariantId();
        const detail = {
          productId: this.productId,
          variantId,
          message,
          font: this.fontSelect?.value || 'abel',
          fontSize: Number(this.sizeInput?.value || 15),
          alignment: this.alignment,
          artworkUrl,
          sourceForm: this.form,
          returnFocus: this.lastTrigger
        };

        this.setGenerating(false);
        this.root.dispatchEvent(new CustomEvent('swalk:personalisation:continue', {
          bubbles: true,
          detail
        }));

      } catch (error) {
        console.error('SWALK personalised artwork generation failed:', error);
        this.showGenerationError(error.message || 'Could not make your card. Please try again.');
        this.setGenerating(false);
      }
    }

    open(trigger, returnFocus = null) {
      ensureLegacyFonts();
      this.editContext = null;
      this.lastTrigger = returnFocus || trigger || null;
      this.sourceForm = this.resolveForm(trigger);
      this.ensureDraftFields();
      this.syncFromDraftFields();
      this.hideGenerationError();
      this.showShellStep('message');
      this.root.hidden = false;
      document.body.classList.add('swalk-personaliser-open');
      document.body.classList.add('swalk-journey-open');
      this.updatePreview();

      ensureSelectedFont(this.fontSelect?.value || 'abel', LEGACY_FONT_SCALE * this.configuredMaximumSize)
        .then(() => {
          this.clampSizeToMessage();
          this.updatePreview();
        });

      requestAnimationFrame(() => {
        if (typeof window.trapFocus === 'function' && this.dialog) {
          window.trapFocus(this.dialog, this.textarea || this.dialog);
        } else {
          this.textarea?.focus();
        }
      });
    }

    close({ preserveJourney = false } = {}) {
      if (this.generating || this.root.hidden) return;
      const editContext = this.editContext;
      this.root.hidden = true;
      document.body.classList.remove('swalk-personaliser-open');
      if (!preserveJourney) document.body.classList.remove('swalk-journey-open');
      this.root.dispatchEvent(new CustomEvent('swalk:shell:closed'));

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(editContext ? null : this.lastTrigger || null);
      } else if (!editContext) {
        this.lastTrigger?.focus?.();
      }

      if (editContext) {
        this.editContext = null;
        this.restoreCartEditContext(editContext);
      }
    }

    restoreCartEditContext(context) {
      if (context.notificationItems && !context.notificationItems.innerHTML) {
        context.notificationItems.innerHTML = context.notificationContent;
      }
      if (context.surface?.matches?.('cart-notification')) {
        context.surface.cartItemLine = String(context.line);
        context.surface.cartItemKey = context.key;
        context.surface.productId = context.productId;
        context.surface.isSuccess = true;
      }
      if (context.surface?.isConnected && typeof context.surface.open === 'function') {
        context.surface.open();
        return;
      }
      document.querySelector(`[data-swalk-edit-message][data-swalk-line="${context.line}"]`)?.focus?.();
    }

    showShellStep(step) {
      const destinationActive = step === 'destination';
      const messageStep = this.root.querySelector('[data-swalk-shell-step="message"]');
      const destinationStep = this.root.querySelector('[data-swalk-shell-step="destination"]');
      const blankConflictStep = this.root.querySelector('[data-swalk-shell-step="blank-conflict"]');
      const blankResolvedStep = this.root.querySelector('[data-swalk-shell-step="blank-resolved"]');
      if (messageStep) messageStep.hidden = destinationActive;
      if (destinationStep) destinationStep.hidden = !destinationActive;
      if (blankConflictStep) blankConflictStep.hidden = true;
      if (blankResolvedStep) blankResolvedStep.hidden = true;
      this.root.dataset.swalkShellStepActive = destinationActive ? 'destination' : 'message';
      this.dialog?.setAttribute('aria-labelledby', messageStep?.querySelector('h2')?.id || '');
    }

    setGenerating(active) {
      this.generating = active;
      this.root.setAttribute('aria-busy', String(active));
      this.closeButtons.forEach((button) => {
        button.disabled = active;
      });

      if (this.continueButton) {
        this.continueButton.textContent = active ? 'MAKING YOUR CARD…' : this.continueLabel;
      }
      this.updateContinueAvailability();
    }

    updateContinueAvailability() {
      if (!this.continueButton) return;
      const disabled = this.generating || !/\S/.test(this.textarea?.value || '');
      this.continueButton.disabled = disabled;
      this.continueButton.setAttribute('aria-disabled', String(disabled));
    }

    showGenerationError(message) {
      if (!this.generationError) return;
      this.generationError.textContent = message;
      this.generationError.hidden = false;
    }

    hideGenerationError() {
      if (!this.generationError) return;
      this.generationError.textContent = '';
      this.generationError.hidden = true;
    }

    markArtworkStale() {
      this.draftArtworkSignature = '';
      this.hideGenerationError();
    }

    async handleFontChange() {
      const sequence = ++this.fontChangeSequence;
      const font = this.fontSelect?.value || 'abel';
      await ensureSelectedFont(font, LEGACY_FONT_SCALE * this.configuredMaximumSize);
      if (sequence !== this.fontChangeSequence || font !== this.fontSelect?.value) return;

      const wasReduced = this.clampSizeToMessage();
      this.updatePreview({ preserveWarning: wasReduced });
      if (wasReduced && this.overflowWarning) this.overflowWarning.hidden = false;
    }

    clampSizeToMessage() {
      if (!this.sizeInput) return false;
      const currentSize = Number(this.sizeInput.value || 15);
      const largestFittingSize = this.getLargestFittingSize();
      if (currentSize <= largestFittingSize) {
        this.lastValidSize = currentSize;
        return false;
      }
      this.sizeInput.value = String(largestFittingSize);
      this.lastValidSize = largestFittingSize;
      this.markArtworkStale();
      return true;
    }

    updatePreview({ preserveWarning = false } = {}) {
      const message = this.textarea?.value || '';
      const font = this.fontSelect?.value || 'abel';
      const size = Number(this.sizeInput?.value || 15);
      const layout = this.measurePrintableLayout(message, font, size);

      if (this.previewMessage) {
        this.previewMessage.textContent = message ? layout.lines.join('\n') : 'Your message goes here.';
        this.previewMessage.classList.toggle('is-placeholder', !message);
        this.previewMessage.style.fontFamily = FONT_FAMILIES[font] || FONT_FAMILIES.abel;
        this.previewMessage.style.textAlign = this.alignment;
      }

      if (this.sizeValue) this.sizeValue.textContent = String(size);
      if (this.sizeInput) this.sizeInput.setAttribute('aria-valuetext', `${size}`);
      this.updateContinueAvailability();
      this.updatePreviewScale();

      requestAnimationFrame(() => {
        if (this.overflowWarning && !preserveWarning) this.overflowWarning.hidden = !this.previewDoesOverflow();
      });
    }

    updatePreviewScale() {
      if (!this.previewPanel || !this.previewMessage) return;
      const size = Number(this.sizeInput?.value || 15);
      const scale = this.previewPanel.clientWidth / PANEL_WIDTH;
      const renderedPx = Math.max(7, LEGACY_FONT_SCALE * size * scale);
      this.previewMessage.style.fontSize = `${renderedPx}px`;
    }

    previewDoesOverflow() {
      return !this.messageFitsPrintableArea(this.textarea?.value || '');
    }

    getMeasurementContext(fontKey, size) {
      const canvas = this.measurementCanvas || (this.measurementCanvas = document.createElement('canvas'));
      const context = canvas.getContext('2d');
      if (!context) return null;
      const fontPx = LEGACY_FONT_SCALE * size;
      const fontName = FONT_NAMES[fontKey] || FONT_NAMES.abel;
      context.font = `${fontPx}px "${fontName}"`;
      return context;
    }

    measurePrintableLayout(message, fontKey = this.fontSelect?.value || 'abel', size = Number(this.sizeInput?.value || 15)) {
      const context = this.getMeasurementContext(fontKey, size);
      if (!context) return { fits: false, lines: [''], lineHeight: 0, ascent: 0, descent: 0, totalHeight: Infinity };

      const fontPx = LEGACY_FONT_SCALE * size;
      const lineHeight = fontPx * LINE_HEIGHT;
      const lines = wrapCanvasText(context, String(message || ''), PRINT_WIDTH);
      let ascent = 0;
      let descent = 0;
      let widestLine = 0;

      lines.forEach((line) => {
        const metrics = context.measureText(line || ' ');
        widestLine = Math.max(widestLine, metrics.width);
        ascent = Math.max(ascent, metrics.actualBoundingBoxAscent || fontPx);
        descent = Math.max(descent, metrics.actualBoundingBoxDescent || fontPx * 0.25);
      });

      const inkHeight = ascent + descent + Math.max(0, lines.length - 1) * lineHeight;
      const lineBoxHeight = lines.length * lineHeight;
      const totalHeight = Math.max(inkHeight, lineBoxHeight);
      return { fits: widestLine <= PRINT_WIDTH && totalHeight <= PRINT_HEIGHT, lines, lineHeight, ascent, descent, totalHeight };
    }

    getPrintableLines(message) {
      return this.measurePrintableLayout(message).lines;
    }

    messageFitsPrintableArea(message) {
      if (!String(message || '')) return true;
      return this.measurePrintableLayout(message).fits;
    }

    getLargestFittingSize() {
      const message = this.textarea?.value || '';
      const font = this.fontSelect?.value || 'abel';
      const minimum = Number(this.sizeInput?.min || 10);
      if (!String(message)) return this.configuredMaximumSize;
      for (let size = this.configuredMaximumSize; size >= minimum; size -= 1) {
        if (this.measurePrintableLayout(message, font, size).fits) return size;
      }
      return minimum;
    }

    getDraftField(key) {
      return this.form?.querySelector(`[data-swalk-personalisation-property="${key}"]`) || null;
    }

    syncFromDraftFields() {
      const messageField = this.getDraftField('message');
      const fontField = this.getDraftField('font');
      const sizeField = this.getDraftField('font-size');
      const alignmentField = this.getDraftField('alignment');

      if (this.textarea) this.textarea.value = messageField?.value || '';
      this.lastValidMessage = this.textarea?.value || '';
      if (this.fontSelect) this.fontSelect.value = fontField?.value || 'abel';
      if (this.sizeInput) {
        this.sizeInput.min = '10';
        this.sizeInput.max = '30';
        this.sizeInput.value = sizeField?.value || '15';
        this.lastValidSize = Number(this.sizeInput.value || 15);
      }
      this.alignment = alignmentField?.value || 'left';

      this.alignmentButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.swalkAlignment === this.alignment));
      });
    }

    saveDraftFields() {
      this.ensureDraftFields();
      const messageField = this.getDraftField('message');
      const fontField = this.getDraftField('font');
      const sizeField = this.getDraftField('font-size');
      const alignmentField = this.getDraftField('alignment');
      const cardTypeField = this.getDraftField('card-type');

      if (messageField) messageField.value = (this.textarea?.value || '').trim();
      if (fontField) fontField.value = this.fontSelect?.value || 'abel';
      if (sizeField) sizeField.value = this.sizeInput?.value || '15';
      if (alignmentField) alignmentField.value = this.alignment;
      if (cardTypeField) cardTypeField.value = this.getArtworkFormat() === 'home-print-v1' && !messageField?.value ? 'Blank' : 'Personalised';
    }

    getSelectedVariantId() {
      if (this.editContext) return this.editContext.variantId;
      return String(this.form?.querySelector('[name="id"]')?.value || '');
    }

    getBaseArtworkUrl(variantId) {
      if (this.editContext && String(variantId) === this.editContext.variantId) {
        return this.editContext.baseArtworkUrl;
      }
      return this.artworkMap?.[String(variantId)] || '';
    }

    getArtworkFormat() {
      if (this.editContext) return this.editContext.artworkFormat;
      const node = this.root.querySelector('[data-swalk-artwork-contract-map]');
      const contracts = JSON.parse(node?.textContent || '{}');
      return contracts[String(this.getSelectedVariantId())]?.format || 'legacy-prodigi-v1';
    }

    storeArtworkContract(data) {
      this.generatedArtworkContract = data;
      if (this.editContext) return;
      const values = { 'artwork-format': data.artworkFormat || '', 'outer-artwork': data.outerArtworkUrl || '', 'inner-artwork': data.innerArtworkUrl || '', 'inner-pdf': data.innerPdfUrl || '' };
      Object.entries(values).forEach(([key, value]) => { const field = this.getDraftField(key); if (field) field.value = value; });
    }

    getSelectedVariantSku(variantId) {
      if (this.editContext && String(variantId) === this.editContext.variantId) {
        return this.editContext.variantSku;
      }
      return this.variantSkuMap?.[String(variantId)] || '';
    }

    buildArtworkSignature(variantId) {
      return JSON.stringify({
        variantId: String(variantId),
        message: (this.textarea?.value || '').trim(),
        font: this.fontSelect?.value || 'abel',
        fontSize: Number(this.sizeInput?.value || 15),
        alignment: this.alignment
      });
    }

    async createOverlayDataUrl() {
      const message = (this.textarea?.value || '').trim();
      const fontKey = this.fontSelect?.value || 'abel';
      const selectedSize = Number(this.sizeInput?.value || 15);
      const fontPx = LEGACY_FONT_SCALE * selectedSize;
      const fontName = FONT_NAMES[fontKey] || FONT_NAMES.abel;

      await ensureSelectedFont(fontKey, fontPx);

      const canvas = document.createElement('canvas');
      canvas.width = PANEL_WIDTH;
      canvas.height = PANEL_HEIGHT;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Your browser could not prepare the card artwork.');

      context.clearRect(0, 0, PANEL_WIDTH, PANEL_HEIGHT);
      context.fillStyle = '#000000';
      context.font = `${fontPx}px "${fontName}"`;
      context.textAlign = this.alignment;

      const layout = this.measurePrintableLayout(message, fontKey, selectedSize);
      const { lines, lineHeight, ascent, totalHeight } = layout;

      if (!layout.fits) {
        throw new Error('That message won’t fit. Make it smaller or say less.');
      }

      let x = SAFE_INSET + SAFETY_BUFFER;
      if (this.alignment === 'center') x = SAFE_INSET + SAFE_WIDTH / 2;
      if (this.alignment === 'right') x = SAFE_INSET + SAFE_WIDTH - SAFETY_BUFFER;

      context.textBaseline = 'alphabetic';
      const startY = SAFE_INSET + SAFETY_BUFFER + (PRINT_HEIGHT - totalHeight) / 2 + ascent;
      lines.forEach((line, index) => {
        context.fillText(line, x, startY + index * lineHeight);
      });

      return canvas.toDataURL('image/png');
    }

    async generateArtwork() {
      if (!this.artworkEndpoint) {
        throw new Error('The card artwork service is not configured yet.');
      }

      const variantId = this.getSelectedVariantId();
      if (!variantId) {
        throw new Error('Choose a card design first.');
      }

      const baseArtworkUrl = this.getBaseArtworkUrl(variantId);
      if (!baseArtworkUrl) {
        throw new Error('This design is missing its print artwork. Please choose another design or try again later.');
      }
      const shopifySku = this.getSelectedVariantSku(variantId);
      if (!shopifySku) {
        throw new Error('This design is missing its SWALK SKU. Please choose another design or try again later.');
      }

      if (!this.editContext) this.ensureDraftFields();
      const artworkFormat = this.getArtworkFormat();
      const message = (this.textarea?.value || '').trim();
      if (artworkFormat === 'home-print-v1' && !message) {
        this.storeArtworkContract({ artworkFormat, outerArtworkUrl: baseArtworkUrl });
        const field = this.editContext ? null : this.getDraftField('artwork-url');
        if (field) field.value = baseArtworkUrl;
        this.draftArtworkSignature = '';
        return baseArtworkUrl;
      }
      const artworkField = this.editContext ? null : this.getDraftField('artwork-url');
      const signature = this.buildArtworkSignature(variantId);

      if (artworkField?.value && this.draftArtworkSignature === signature) {
        return artworkField.value;
      }

      const overlayDataUrl = await this.createOverlayDataUrl();
      const response = await fetch(this.artworkEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artworkFormat,
          message,
          baseArtworkUrl,
          overlayDataUrl,
          previousArtworkUrl: this.editContext?.properties?.['_SWALK Print Artwork'] || artworkField?.value || '',
          shopifySku
        })
      });

      let data = {};
      try {
        data = await response.json();
      } catch {}

      if (!response.ok || !data.url) {
        throw new Error(data.details || data.error || 'Could not make your card. Please try again.');
      }

      if (artworkField) artworkField.value = data.url;
      this.storeArtworkContract(data);
      if (artworkField) this.draftArtworkSignature = signature;
      return data.url;
    }

    async saveCartEdit({ message, font, fontSize, alignment, artworkUrl }) {
      const context = this.editContext;
      if (!context?.line) throw new Error('This basket item could not be updated. Please try again.');

      const retainedProperties = { ...context.properties };
      REMOVED_LINE_PROPERTIES.forEach((key) => delete retainedProperties[key]);
      const properties = {
        ...retainedProperties,
        Message: message,
        '_SWALK Font': font,
        '_SWALK Font Size': String(fontSize),
        '_SWALK Alignment': alignment,
        '_SWALK Print Artwork': artworkUrl
      };
      if (this.getArtworkFormat() === 'home-print-v1') {
        properties['_SWALK Artwork Format'] = 'home-print-v1';
        properties['_SWALK Card Type'] = message ? 'Personalised' : 'Blank';
        properties['_SWALK Outer Artwork'] = context.baseArtworkUrl;
        delete properties['_SWALK Inner Artwork'];
        delete properties['_SWALK Inner Print PDF'];
        if (this.generatedArtworkContract?.innerArtworkUrl) properties['_SWALK Inner Artwork'] = this.generatedArtworkContract.innerArtworkUrl;
        if (this.generatedArtworkContract?.innerPdfUrl) properties['_SWALK Inner Print PDF'] = this.generatedArtworkContract.innerPdfUrl;
      }
      if (!isPlainObject(context.properties) || !isPlainObject(properties) || Object.keys(properties).some((key) => /^\d+$/.test(key))) {
        throw new Error('This basket item could not be updated safely. Please close the editor and try again.');
      }
      const sections = ['cart-drawer', 'cart-notification-items', 'main-cart-items'];
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          line: context.line,
          quantity: context.quantity,
          properties,
          sections,
          sections_url: window.location.pathname
        })
      });

      let state = {};
      try {
        state = await response.json();
      } catch {}
      if (!response.ok || state.errors || state.status) {
        throw new Error(state.description || state.message || state.errors || 'This basket item could not be updated. Please try again.');
      }

      context.properties = properties;
      context.key = state.items?.[context.line - 1]?.key || context.key;
      this.refreshEditedCartLine(context, state.sections || {});
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'swalk-cart-editor' });
      }
    }

    refreshEditedCartLine(context, sections) {
      const selector = `.cart-item[data-index="${context.line}"]`;
      const parseItem = (html) => {
        if (!html) return null;
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      };
      const sources = {
        drawer: parseItem(sections['cart-drawer']),
        notification: parseItem(sections['cart-notification-items']),
        page: parseItem(sections['main-cart-items'])
      };

      document.querySelectorAll(selector).forEach((item) => {
        const source = item.closest('cart-notification')
          ? sources.notification
          : item.closest('cart-drawer')
            ? sources.drawer
            : sources.page;
        if (source) item.replaceWith(source.cloneNode(true));
      });

      if (context.notificationItems && sources.notification) {
        context.notificationContent = sources.notification.outerHTML;
      }
    }
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.swalkPersonaliserReady === 'true') return;
      root.dataset.swalkPersonaliserReady = 'true';
      new SwalkPersonaliser(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('swalk:personaliser:init', init);
})();
